import { useEffect } from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/seo";
import { useAuth } from "../contexts/AuthContext";
import { useFeed } from "../hooks/useFeed";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useFeedSEO } from "../hooks/useSEO";
import { useUIStore } from "../stores/uiStore";
import CommitCard from "../components/commit/CommitCard";
import CommitThreadSkeleton from "../components/commit/CommitThreadSkeleton";
import CommitThread from "../components/commit/CommitThread";

export default function Feed() {
  const { user, isLoading: authLoading } = useAuth();
  const { commits, loading, error, hasMore, loadMore, refetch } = useFeed(3);
  const seoConfig = useFeedSEO();

  // Infinite scroll
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: loading,
  });

  // Get store actions
  const openCommitEditor = useUIStore((s) => s.openCommitEditor);
  const openDeleteConfirm = useUIStore((s) => s.openDeleteConfirm);
  const openLinkedInShare = useUIStore((s) => s.openLinkedInShare);
  const openEditProfile = useUIStore((s) => s.openEditProfile);
  const subscribeFeed = useUIStore((s) => s.subscribeFeed);
  const deletedCommitIds = useUIStore((s) => s.deletedCommitIds);

  // Auto-open edit profile modal for new users (onboarding) - only once ever
  // Wait for auth to complete to ensure we have the verified user ID
  useEffect(() => {
    if (user && !authLoading) {
      const onboardingKey = `onboarding-shown-${user.id}`;
      const hasSeenOnboarding = localStorage.getItem(onboardingKey);
      if (!hasSeenOnboarding) {
        localStorage.setItem(onboardingKey, 'true');
        openEditProfile();
      }
    }
  }, [user, authLoading, openEditProfile]);

  // Subscribe to feed updates
  useEffect(() => {
    const unsubscribe = subscribeFeed(() => refetch());
    return unsubscribe;
  }, [subscribeFeed, refetch]);

  // Filter out deleted commits for optimistic UI
  const visibleCommits = commits.filter((c) => !deletedCommitIds.has(c.id));

  return (
    <>
      <SEOHead config={seoConfig} />
      {/* Main content */}
      <main className="max-w-[720px] mx-auto px-0 sm:px-4 py-4 sm:py-6">
        {/* Error state */}
        {error && (
          <div className="mx-4 sm:mx-0 rounded-lg border border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/10 p-4 mb-6">
            <p className="text-sm text-[hsl(var(--destructive))] mb-3">
              Failed to load your feed
            </p>
            <Button variant="outline" size="sm" onClick={refetch}>
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        )}

        {/* Loading state */}
        {loading && commits.length === 0 && (
          <CommitThreadSkeleton count={3} />
        )}

        {/* Commits list */}
        {visibleCommits.length > 0 && (
          <div>
            <CommitThread hasMore={hasMore}>
              {visibleCommits.map((commit) => (
                <CommitCard
                  key={commit.id}
                  commit={commit}
                  currentUserId={user?.id}
                  onShare={openLinkedInShare}
                  onEdit={openCommitEditor}
                  onDelete={openDeleteConfirm}
                />
              ))}
            </CommitThread>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-px" />

            {/* Loading indicator */}
            {loading && commits.length > 0 && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--muted-foreground))]" />
              </div>
            )}
          </div>
        )}

      </main>
    </>
  );
}
